import { Space, Button, Popconfirm, InputNumber, Input, AutoComplete, Select } from "antd";
import { EditOutlined, DeleteOutlined, PrinterOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { PACK_TYPE_LABEL } from "../../../utils/pack-type";

import { docLinkRender } from "../../../components/table/DocLink";
const num = (v) => Number(v || 0);
const money = (v, d = 2) =>
  num(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

export const accessColumn = ({ handleView, handleEdit, handleDelete, handlePrint }) => [
  { title: "เลขที่ Invoice", dataIndex: "inv_code", key: "inv_code", width: 150,
    /* กดเลขที่เอกสารเพื่อเปิดหน้าแสดงข้อมูล */
    render: docLinkRender(handleView, "เปิดดู Invoice"),
  },
  { title: "Job Order", dataIndex: "jocode", key: "jocode", width: 110, align: "center" },
  { title: "ลูกค้า", dataIndex: "cusname", key: "cusname", width: 200 },
  { title: "Brand", dataIndex: "brand", key: "brand", width: 140 },
  {
    title: "วันที่ Invoice", dataIndex: "inv_date", key: "inv_date", width: 120, align: "center",
    render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-"),
  },
  {
    title: "ยอดรวม", dataIndex: "total_amount", key: "total_amount", width: 130, align: "right",
    render: (v, r) => `${r.currency || ""} ${money(v)}`,
  },
  { title: "สร้างโดย", dataIndex: "created_name", key: "created_name", width: 150 },
  {
    title: "Action", key: "action", fixed: "right", width: 180,
    render: (_, record) => (
      <Space>
        <Button
          size="small"
          icon={<EyeOutlined />}
          className="bn-success-outline"
          title="ดูข้อมูล"
          onClick={() => handleView(record)}
        />
        <Button
          size="small"
          icon={<EditOutlined />}
          className="bn-primary-outline"
          onClick={() => handleEdit(record)}
        />
        <Button
          size="small"
          icon={<PrinterOutlined />}
          className="bn-warning-outline"
          onClick={() => handlePrint(record)}
        />
        <Popconfirm
          title="ยืนยันการลบ?"
          onConfirm={() => handleDelete(record)}
          okText="ลบ"
          cancelText="ยกเลิก"
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ),
  },
];

/* ตัวเลือกขนาดบรรจุที่ใช้บ่อย — เลือกจากลิสต์เพื่อลดพิมพ์ผิด แต่พิมพ์เองก็ได้
   (ค่าที่ดึงมาจาก Packing Goods จะถูกเติมเข้าลิสต์ให้อัตโนมัติจากหน้า MyManage) */
export const SALE_UNITS = ["CASE", "CTN", "PALLET", "PCS", "BTL", "KGS", "SET"];

export const detailColumns = ({
  updateDetail,
  deleteDetail,
  currency,
  packingSizeOptions = [],
  /* โหมดดูอย่างเดียว — ซ่อนคอลัมน์ปุ่มลบ */
  readOnly = false,
}) => [
  { title: "ลำดับ", key: "seq", dataIndex: "seq", width: 55, align: "center" },
  { title: "รหัสสินค้า", key: "pg_code", dataIndex: "pg_code", width: 120 },
  /* แบรนด์รายบรรทัด — ใบเดียวมีได้หลายแบรนด์ พิมพ์นำหน้าชื่อสินค้าในใบกำกับ */
  {
    title: "Brand", key: "brand", width: 150,
    render: (_, record, index) => (
      <Input
        value={record.brand}
        placeholder="เช่น TIGER KHAN"
        onChange={(e) => updateDetail(index, "brand", e.target.value)}
      />
    ),
  },
  /* ชื่อสินค้าแก้ไขได้ เผื่อกรณีที่ต้องปรับข้อความบนใบกำกับเฉพาะใบนั้น */
  {
    title: "รายการสินค้า", key: "pg_name", width: 260,
    render: (_, record, index) => (
      <Input
        value={record.pg_name}
        onChange={(e) => updateDetail(index, "pg_name", e.target.value)}
      />
    ),
  },
  /* ขนาดบรรจุ : ค่าตั้งต้นมาจาก Packing ที่ผูกกับ Packing Goods
     เลือกจากลิสต์ได้เพื่อกันพิมพ์ผิด และพิมพ์เองได้ถ้าไม่มีในลิสต์ */
  {
    title: "Packing Size", key: "packing_size", width: 150, align: "center",
    render: (_, record, index) => (
      <AutoComplete
        value={record.packing_size}
        style={{ width: "100%" }}
        options={packingSizeOptions.map((v) => ({ value: v }))}
        placeholder="เช่น 12X300ML"
        filterOption={(input, option) =>
          String(option?.value || "").toUpperCase().includes(String(input || "").toUpperCase())
        }
        onChange={(val) => updateDetail(index, "packing_size", val)}
      />
    ),
  },
  {
    title: "Pcs/Ctn", key: "unit_cs", dataIndex: "unit_cs", width: 80, align: "right",
    render: (v) => money(v, 0),
  },
  {
    title: "กล่อง", key: "carton", dataIndex: "carton", width: 80, align: "right",
    render: (v) => money(v, 0),
  },
  {
    title: "จำนวนรวม (Pcs)", key: "total_qty", width: 110, align: "right",
    render: (_, record) => money(num(record.unit_cs) * num(record.carton), 0),
  },
  /* ── หน่วยขาย : ตัวเลขและหน่วยที่พิมพ์ในคอลัมน์ QUANTITY ของใบกำกับ
     และเป็นตัวที่เอาไปคูณกับราคาต่อหน่วย (ไม่ใช่จำนวนชิ้นรวมซึ่งเป็นหน่วยเก็บ) ── */
  {
    title: "จำนวนขาย", key: "sale_qty", width: 110, align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        value={record.sale_qty}
        style={{ width: "100%" }}
        controls={false}
        placeholder="ว่าง = ใช้จำนวนกล่อง"
        onChange={(val) => updateDetail(index, "sale_qty", val)}
      />
    ),
  },
  {
    title: "หน่วยขาย", key: "sale_unit", width: 100, align: "center",
    render: (_, record, index) => (
      <Select
        value={record.sale_unit || "CASE"}
        style={{ width: "100%" }}
        options={SALE_UNITS.map((u) => ({ label: u, value: u }))}
        onChange={(val) => updateDetail(index, "sale_unit", val)}
      />
    ),
  },
  /* น้ำหนักพาเลตต่อหน่วย มาจากประเภทหน่วยบรรจุของสินค้า แก้ในใบนี้ไม่ได้
     ถ้าจะเปลี่ยนต้องไปแก้ "ประเภทหน่วยบรรจุ" ที่ข้อมูลหลัก Packing Goods */
  {
    /* น้ำหนักบรรจุภัณฑ์ขนส่งต่อหน่วย : พาเลตไม้/พลาสติก หรือกล่องเปล่า */
    title: "บรรจุภัณฑ์/หน่วย (KG)", key: "pallet_wt", width: 130, align: "right",
    render: (_, record) =>
      num(record.pallet_wt) > 0 ? (
        <span title={PACK_TYPE_LABEL[record.pack_type] || record.pack_type || ""}>
          {money(record.pallet_wt, 1)}
        </span>
      ) : (
        "-"
      ),
  },
  {
    /* น้ำหนัก/กล่อง ดึงจากทะเบียน PG ให้ตั้งต้น แต่แก้เองได้ทุกบรรทัด
       (บางงานชั่งจริงแล้วไม่ตรงกับค่าในทะเบียน) */
    title: "NW/กล่อง (KG)", key: "net_wt", width: 110, align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        value={record.net_wt}
        style={{ width: "100%" }}
        controls={false}
        onChange={(val) => updateDetail(index, "net_wt", val)}
      />
    ),
  },
  {
    title: "GW/กล่อง (KG)", key: "gross_wt", width: 110, align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        value={record.gross_wt}
        style={{ width: "100%" }}
        controls={false}
        onChange={(val) => updateDetail(index, "gross_wt", val)}
      />
    ),
  },
  {
    title: "NW รวม (KG)", key: "net_weight", width: 110, align: "right",
    render: (_, record) => money(num(record.carton) * num(record.net_wt)),
  },
  {
    /* GW รวม = จำนวนกล่อง x (น้ำหนักรวมต่อกล่อง + น้ำหนักพาเลตต่อหน่วย)
       NW ไม่บวกพาเลต เพราะเป็นน้ำหนักสินค้าล้วน */
    title: "GW รวม (KG)", key: "gross_weight", width: 110, align: "right",
    render: (_, record) =>
      money(num(record.carton) * (num(record.gross_wt) + num(record.pallet_wt))),
  },
  {
    title: `ราคา/หน่วย (${currency || "USD"})`, key: "unit_price", width: 130, align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        value={record.unit_price}
        style={{ width: "100%" }}
        controls={false}
        onChange={(val) => updateDetail(index, "unit_price", val)}
      />
    ),
  },
  {
    title: `รวมเงิน (${currency || "USD"})`, key: "line_total", width: 120, align: "right",
    /* จำนวนหน่วยขาย x ราคาต่อหน่วย — ตรงกับตัวเลขที่พิมพ์บนใบกำกับ */
    render: (_, record) =>
      money(
        (num(record.sale_qty) > 0 ? num(record.sale_qty) : num(record.carton)) *
          num(record.unit_price)
      ),
  },
  ...(readOnly
    ? []
    : [
        {
          title: "", key: "action", width: 45, align: "center",
          render: (_, record) => (
            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => deleteDetail(record.seq)} />
          ),
        },
      ]),
];

/* ── ตารางธนาคารบน Invoice ──
   ทุกช่องดึงจากข้อมูลหลักธนาคารตอนเลือก แก้ไขในใบนี้ไม่ได้
   ถ้าต้องแก้ สาขา / ที่อยู่ / SWIFT ให้ไปแก้ที่ข้อมูลหลักธนาคาร (ปุ่มแก้ไขใน Modal เลือกธนาคาร) */
export const bankColumns = ({ removeBank, readOnly = false }) => [
  {
    title: "No.", key: "no", width: 50, align: "center",
    render: (_, __, index) => index + 1,
  },
  {
    title: "Bank", key: "bank", width: 190,
    render: (_, record) => (
      <Space size={8}>
        <i className={`bank bank-${record.bank} shadow huge`} style={{ height: 24, width: 24, marginTop: 4 }} />
        <span>
          <div style={{ fontSize: 13 }}>{record.bank_name_th}</div>
          <div style={{ fontSize: 9, color: "#8c8386" }}>{record.bank_name}</div>
        </span>
      </Space>
    ),
  },
  { title: "Account Number", dataIndex: "acc_no", key: "acc_no", width: 140 },
  { title: "Account Name", dataIndex: "acc_name", key: "acc_name", width: 220 },
  {
    title: "Branch (สาขา)", key: "bank_branch", dataIndex: "bank_branch", width: 190,
    render: (v) => v || "-",
  },
  {
    title: "Bank Address", key: "bank_address", dataIndex: "bank_address", width: 250,
    render: (v) => v || "-",
  },
  {
    title: "SWIFT", key: "swift_code", dataIndex: "swift_code", width: 120,
    render: (v) => v || "-",
  },
  ...(readOnly
    ? []
    : [
        {
          title: "", key: "action", width: 45, align: "center", fixed: "right",
          render: (_, record) => (
            <Popconfirm
              placement="topRight"
              title="ลบธนาคารนี้ออกจาก Invoice?"
              onConfirm={() => removeBank(record)}
              okText="ลบ"
              cancelText="ยกเลิก"
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          ),
        },
      ]),
];
